import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share } from "lucide-react";
import { useTr } from "@/i18n";

const DISMISS_KEY = "djmagix-install-dismissed";

function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallApp() {
  const tr = useTr();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || sessionStorage.getItem(DISMISS_KEY)) return;

    // Android/Chrome: il browser dichiara che la PWA è installabile
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    const onInstalled = () => setShow(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari: nessun evento, mostriamo le istruzioni "Aggiungi a Home"
    let iosTimer;
    if (isIos() && !isStandalone()) {
      iosTimer = setTimeout(() => setShow(true), 4000);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice.catch(() => null);
      setDeferredPrompt(null);
      setShow(false);
    }
  };

  const ios = isIos();

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="install-app-banner"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{ duration: 0.4 }}
          className="fixed top-20 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-sm z-[65] gold-frame rounded-2xl bg-[#0D0D0D]/95 backdrop-blur-xl p-5"
          role="dialog"
          aria-label={tr("install.title", "Scarica l'app DJ Magix")}
        >
          <button
            data-testid="install-app-dismiss"
            onClick={dismiss}
            aria-label={tr("install.dismiss", "Non ora")}
            className="absolute top-3 right-3 text-[#9F8A52] hover:text-[#F7DE8B] transition-colors"
          >
            <X size={16} />
          </button>
          <div className="flex items-start gap-4">
            <img
              src="/assets/icon-192.png"
              alt="DJ Magix"
              width={48}
              height={48}
              className="h-12 w-12 rounded-xl border border-[rgba(226,184,87,0.4)]"
            />
            <div className="flex-1">
              <p className="font-display text-sm font-semibold text-[#F7DE8B]">
                {tr("install.title", "DJ Magix sempre in tasca")}
              </p>
              <p className="text-xs text-[#E2C882]/80 leading-relaxed mt-1">
                {ios
                  ? tr("install.ios", "Su iPhone: apri il menu Condividi (quadrato con freccia) e scegli 'Aggiungi a Home'.")
                  : tr("install.text", "Installa l'app gratuita: mix, gallery e preventivi in un tocco.")}
              </p>
              {!ios && deferredPrompt && (
                <button
                  data-testid="install-app-button"
                  onClick={install}
                  className="gold-btn rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] mt-3 inline-flex items-center gap-2"
                >
                  <Download size={14} /> {tr("install.button", "Installa")}
                </button>
              )}
              {ios && (
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#BFA45D] mt-3 inline-flex items-center gap-1.5">
                  <Share size={13} /> {tr("install.button", "Installa")}
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
